import { watchState } from "@ngrx/signals";
import { ButtonModule } from "primeng/button";
import { PasswordModule } from 'primeng/password';
import { NgOptimizedImage } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Router, RouterLink } from "@angular/router";
import { environment } from '@environments/environment';
import { AuthState, AuthStore } from "@auth/stores/auth.store";
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { LoginBuildFormService } from '@auth/services/login-build-form.service';
import type { GoogleCredentialResponse, GoogleIdentityServices } from '@auth/interfaces/google-identity.interface';
import { ChangeDetectionStrategy, Component, Injector, NgZone, OnInit, inject, signal } from '@angular/core';
import {
  APP_BRAND_LOGO_ALT,
  APP_BRAND_NAME,
  APP_BRAND_ORG,
  APP_BRAND_TAGLINE,
} from '@shared/constants/app-brand.const';

declare const google: GoogleIdentityServices;

@Component({
  selector: 'app-login',
  imports: [
    RouterLink,
    ButtonModule,
    PasswordModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    NgOptimizedImage,
    ReactiveFormsModule,
  ],
  templateUrl: './login.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);
  readonly authStore = inject(AuthStore);
  private readonly loginBuildFormService = inject(LoginBuildFormService);

  public readonly isSubmitting = signal(false);
  public readonly loginForm = this.loginBuildFormService.buildLoginForm();

  /** Marca y textos de UI compartidos con otras pantallas de auth. */
  readonly brandLogoAlt = APP_BRAND_LOGO_ALT;
  readonly brandName = APP_BRAND_NAME;
  readonly brandTagline = APP_BRAND_TAGLINE;
  readonly brandFooterLine = `${APP_BRAND_NAME} · ${APP_BRAND_ORG}`;

  /**
   * Getter para acceder al control de email del formulario.
   */
  get email(): FormControl {
    return this.loginForm.controls['email'] as FormControl;
  }

  /**
   * Getter para acceder al control de contraseña del formulario.
   */
  get password(): FormControl {
    return this.loginForm.controls['password'] as FormControl;
  }

  /**
   * Método del ciclo de vida Angular que se ejecuta al inicializar el componente.
   */
  public ngOnInit(): void {
    this.redirectIfAuthenticated();
    this.authStore.initializeAuth();
    this.watchAuthStateChanges();
    this.initGoogleSignIn();
  }

  /**
   * Activa el flujo de inicio de sesión con Google via GIS One Tap.
   */
  public onGoogleSignIn(): void {
    if (typeof google === 'undefined' || !environment.googleClientId) return;
    google.accounts.id.prompt();
  }

  /**
   * Maneja el evento de envío del formulario de inicio de sesión.
   */
  public onSubmit(): void {
    if (this.loginForm.invalid) return;
    this.authStore.login(this.loginForm.value);
  }

  /**
   * Inicializa Google Identity Services y renderiza el botón de Sign In con Google.
   */
  private initGoogleSignIn(): void {
    if (typeof google === 'undefined' || !environment.googleClientId) return;

    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: GoogleCredentialResponse) => {
        this.ngZone.run(() => {
          this.authStore.googleLogin(response.credential);
        });
      },
      cancel_on_tap_outside: true,
    });
  }

  /**
   * Redirige al usuario si ya está autenticado.
   */
  private redirectIfAuthenticated(): void {
    if (this.authStore.isAuthenticated()) {
      this.router.navigate(['/app/inicio']);
    }
  }

  /**
   * Observa los cambios de estado en el store de autenticación.
   */
  private watchAuthStateChanges(): void {
    watchState(this.authStore, this.handleAuthStateChange.bind(this), {
      injector: this.injector
    });
  }

  /**
   * Maneja los cambios de estado de autenticación.
   */
  private handleAuthStateChange(authState: AuthState): void {
    if (authState.isLoading) {
      this.loginForm.controls['password'].reset('');
    }
    this.isSubmitting.set(authState.isLoading);
  }
}
